/**
 * Human-readable formatting for pricing calculator configurations.
 *
 * Pure function — no React, no side effects. Formats raw calculator
 * config objects into human-readable strings and structured data.
 *
 * @see AC-1 through AC-7 in docs/specifications/2026-03-22-billing-admin-pricing-preview.md
 */

// =============================================================================
// Types
// =============================================================================

export interface TableRow {
  key: string;
  value: string;
}

export interface CalculatorDisplayResult {
  /** Human-readable summary string */
  summary: string;
  /** Display type: 'text' for simple strings, 'table' for structured data, 'unknown' for fallback */
  type: "text" | "table" | "unknown";
  /** Structured table rows (only for DISCRETE_POINTS) */
  tableRows?: TableRow[];
  /** Raw JSON string for collapsible display (AC-6) */
  rawJson: string;
}

// =============================================================================
// Currency formatting helper
// =============================================================================

/**
 * Formats cents amount as PLN currency string using Intl.NumberFormat.
 * Always uses currencyDisplay: "code" per project convention.
 *
 * @example formatCentsAsCurrency(4900) => "49,00 PLN"
 */
export function formatCentsAsCurrency(cents: number): string {
  const raw = new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
  // Normalize non-breaking spaces (U+00A0, U+202F) to regular spaces
  // for consistent display and testability
  return raw.replace(/[\u00A0\u202F]/g, " ");
}

// =============================================================================
// Constants
// =============================================================================

/** Maximum fraction digits for percentage display — accommodates very precise rates */
const MAX_PERCENT_FRACTION_DIGITS = 10;

// =============================================================================
// Main function
// =============================================================================

/**
 * Formats a calculator config into a human-readable display result.
 *
 * Handles known types: FIXED_AMOUNT, PERCENTAGE, FREE, DISCRETE_POINTS,
 * DEGRESSIVE_EXPONENTIAL. Unknown types fall back to type name + raw JSON.
 *
 * @param calculatorType - The calculator type string from PricingComponentVersion
 * @param config - The raw calculator configuration object
 * @returns Structured display result with summary, type, and raw JSON
 */
export function formatCalculatorConfig(
  calculatorType: string,
  config: Record<string, unknown>
): CalculatorDisplayResult {
  const rawJson = JSON.stringify(config, null, 2);

  switch (calculatorType) {
    case "FIXED_AMOUNT": {
      const amountCents = (config.amountCents as number) ?? 0;
      const formatted = formatCentsAsCurrency(amountCents);
      return {
        summary: `${formatted} netto`,
        type: "text",
        rawJson,
      };
    }

    case "PERCENTAGE": {
      const rate = (config.rate as number) ?? 0;
      const percent = rate * 100;
      // Format: no decimal places if whole number, otherwise show decimals
      const formatted = new Intl.NumberFormat("pl-PL", {
        maximumFractionDigits: MAX_PERCENT_FRACTION_DIGITS,
        minimumFractionDigits: 0,
      }).format(percent);
      return {
        summary: `${formatted}%`,
        type: "text",
        rawJson,
      };
    }

    case "FREE": {
      return {
        summary: "Bezpłatny",
        type: "text",
        rawJson,
      };
    }

    case "DISCRETE_POINTS": {
      const points = (config.points as Record<string, number>) ?? {};
      const entries = Object.entries(points);

      if (entries.length === 0) {
        return {
          summary: "Brak punktów cenowych",
          type: "text",
          rawJson,
        };
      }

      const tableRows: TableRow[] = entries.map(([key, valueCents]) => ({
        key,
        value: formatCentsAsCurrency(valueCents) + " netto",
      }));

      const summary = tableRows
        .map((row) => `${row.key} → ${row.value}`)
        .join(" | ");

      return {
        summary,
        type: "table",
        tableRows,
        rawJson,
      };
    }

    case "DEGRESSIVE_EXPONENTIAL": {
      const A = typeof config.A === "number" ? config.A : 0;
      const k = typeof config.k === "number" ? config.k : 0;
      const C = typeof config.C === "number" ? config.C : 0;

      const formatNum = (n: number) =>
        new Intl.NumberFormat("pl-PL", {
          maximumFractionDigits: MAX_PERCENT_FRACTION_DIGITS,
          minimumFractionDigits: 0,
        }).format(n);

      return {
        summary: `Degresywny (A\u00B7e^(-kx)+C): A=${formatNum(A)}, k=${formatNum(k)}, C=${formatNum(C)}`,
        type: "text",
        rawJson,
      };
    }

    default: {
      return {
        summary: calculatorType,
        type: "unknown",
        rawJson,
      };
    }
  }
}
